/**
 * Database Connector Plugin - Entry Point
 * 
 * This plugin enables AI agents to:
 * - Query external databases in real-time during conversations
 * - Update/insert data based on customer requests
 * - Generate and send PDF documents (invoices, statements)
 * - Schedule follow-up callbacks (for debt collection, reminders)
 */

import { Router, Request, Response, NextFunction } from 'express';
import { ConnectionManagerService } from './services/connection-manager.service';
import { CallbackSchedulerService } from './services/callback-scheduler.service';
import { DocumentGeneratorService } from './services/document-generator.service';
import { EncryptionService } from './services/encryption.service';
import { handleQueryDatabaseTool, getQueryDatabaseTool } from './tools/query-database.tool';
import { handleScheduleCallbackTool, getScheduleCallbackTool } from './tools/schedule-callback.tool';

export interface PluginLoaderOptions {
  db: any;
  drizzle: any;
  storage: any;
  emailService?: any;
  campaignExecutor?: any;
}

let pluginServices: {
  connectionManager: ConnectionManagerService;
  callbackScheduler: CallbackSchedulerService;
  documentGenerator: DocumentGeneratorService;
} | null = null;

/**
 * Initialize plugin services
 */
function initializeServices(options: PluginLoaderOptions) {
  if (pluginServices) return pluginServices;

  pluginServices = {
    connectionManager: new ConnectionManagerService(options.db),
    callbackScheduler: new CallbackSchedulerService(options.db, options.campaignExecutor),
    documentGenerator: new DocumentGeneratorService(options.db, options.emailService),
  };

  // Start the callback scheduler
  pluginServices.callbackScheduler.start();

  console.log('[Database Connector] Plugin services initialized');

  return pluginServices;
}

/**
 * Register Database Connector routes
 */
export function registerDatabaseConnectorRoutes(
  app: any,
  options: PluginLoaderOptions
): void {
  const router = Router();
  const services = initializeServices(options);

  // Middleware to verify authentication
  const authenticateToken = (req: Request, res: Response, next: NextFunction) => {
    // Use the app's authentication middleware
    // This should be passed from the main app
    next();
  };

  // ============================================================
  // Connection Management Routes
  // ============================================================

  // Get all connections for user
  router.get('/connections', authenticateToken, async (req: any, res: Response) => {
    try {
      // TODO: Implement get connections
      res.json({ connections: [] });
    } catch (error: any) {
      res.status(500).json({ error: error.message });
    }
  });

  // Create new connection
  router.post('/connections', authenticateToken, async (req: any, res: Response) => {
    try {
      const { name, description, config, allowedTables, allowedOperations } = req.body;

      const connection = await services.connectionManager.createConnection(
        req.userId,
        name,
        config,
        { description, allowedTables, allowedOperations }
      );

      res.json(connection);
    } catch (error: any) {
      res.status(500).json({ error: error.message });
    }
  });

  // Test connection
  router.post('/connections/:id/test', authenticateToken, async (req: any, res: Response) => {
    try {
      const result = await services.connectionManager.testConnection(req.params.id);
      res.json(result);
    } catch (error: any) {
      res.status(500).json({ error: error.message });
    }
  });

  // Discover schema
  router.post('/connections/:id/discover', authenticateToken, async (req: any, res: Response) => {
    try {
      const schemas = await services.connectionManager.discoverSchema(req.params.id);
      res.json({ schemas });
    } catch (error: any) {
      res.status(500).json({ error: error.message });
    }
  });

  // ============================================================
  // Saved Queries Routes
  // ============================================================

  router.get('/queries', authenticateToken, async (req: any, res: Response) => {
    try {
      // TODO: Implement get queries
      res.json({ queries: [] });
    } catch (error: any) {
      res.status(500).json({ error: error.message });
    }
  });

  router.post('/queries', authenticateToken, async (req: any, res: Response) => {
    try {
      // TODO: Implement create query
      res.json({ success: true });
    } catch (error: any) {
      res.status(500).json({ error: error.message });
    }
  });

  // ============================================================
  // Saved Actions Routes
  // ============================================================

  router.get('/actions', authenticateToken, async (req: any, res: Response) => {
    try {
      // TODO: Implement get actions
      res.json({ actions: [] });
    } catch (error: any) {
      res.status(500).json({ error: error.message });
    }
  });

  router.post('/actions', authenticateToken, async (req: any, res: Response) => {
    try {
      // TODO: Implement create action
      res.json({ success: true });
    } catch (error: any) {
      res.status(500).json({ error: error.message });
    }
  });

  // ============================================================
  // Document Templates Routes
  // ============================================================

  router.get('/documents/templates', authenticateToken, async (req: any, res: Response) => {
    try {
      // TODO: Implement get templates
      res.json({ templates: [] });
    } catch (error: any) {
      res.status(500).json({ error: error.message });
    }
  });

  router.post('/documents/templates', authenticateToken, async (req: any, res: Response) => {
    try {
      // TODO: Implement create template
      res.json({ success: true });
    } catch (error: any) {
      res.status(500).json({ error: error.message });
    }
  });

  router.post('/documents/generate', authenticateToken, async (req: any, res: Response) => {
    try {
      const { templateId, data, deliveryMethod, deliveryTo } = req.body;

      const document = await services.documentGenerator.generateDocument(
        templateId,
        data,
        { userId: req.userId },
        { deliveryMethod, deliveryTo }
      );

      res.json(document);
    } catch (error: any) {
      res.status(500).json({ error: error.message });
    }
  });

  // ============================================================
  // Scheduled Callbacks Routes
  // ============================================================

  router.get('/callbacks', authenticateToken, async (req: any, res: Response) => {
    try {
      const callbacks = await services.callbackScheduler.getCallbacksForUser(req.userId, {
        status: req.query.status as any,
        limit: parseInt(req.query.limit as string) || 50,
      });
      res.json({ callbacks });
    } catch (error: any) {
      res.status(500).json({ error: error.message });
    }
  });

  router.post('/callbacks', authenticateToken, async (req: any, res: Response) => {
    try {
      const callback = await services.callbackScheduler.scheduleCallback(
        req.userId,
        req.body.agentId,
        req.body
      );
      res.json(callback);
    } catch (error: any) {
      res.status(500).json({ error: error.message });
    }
  });

  router.delete('/callbacks/:id', authenticateToken, async (req: any, res: Response) => {
    try {
      await services.callbackScheduler.cancelCallback(req.params.id, req.userId);
      res.json({ success: true });
    } catch (error: any) {
      res.status(500).json({ error: error.message });
    }
  });

  // ============================================================
  // Agent Tool Webhook Routes (called by ElevenLabs)
  // ============================================================

  // Query database tool webhook
  router.post('/tools/query/:secret/:agentId', async (req: Request, res: Response) => {
    try {
      const { secret, agentId } = req.params;

      // Validate secret
      const expectedSecret = process.env.DB_CONNECTOR_WEBHOOK_SECRET;
      if (expectedSecret && secret !== expectedSecret) {
        return res.status(401).json({ error: 'Unauthorized' });
      }

      // Build execution context from call metadata
      const context = {
        userId: req.body._metadata?.userId || '',
        agentId,
        callId: req.body._metadata?.callId,
        callerPhone: req.body._metadata?.callerPhone,
        customerId: req.body._metadata?.customerId,
      };

      const result = await handleQueryDatabaseTool(
        req.body,
        context,
        { connectionManager: services.connectionManager, queryEngine: null }
      );

      res.json(result);
    } catch (error: any) {
      console.error('[Database Connector] Query tool error:', error);
      res.status(500).json({
        success: false,
        response: 'حدث خطأ في الاستعلام',
      });
    }
  });

  // Schedule callback tool webhook
  router.post('/tools/callback/:secret/:agentId', async (req: Request, res: Response) => {
    try {
      const { secret, agentId } = req.params;

      // Validate secret
      const expectedSecret = process.env.DB_CONNECTOR_WEBHOOK_SECRET;
      if (expectedSecret && secret !== expectedSecret) {
        return res.status(401).json({ error: 'Unauthorized' });
      }

      // Build execution context
      const context = {
        userId: req.body._metadata?.userId || '',
        agentId,
        callId: req.body._metadata?.callId,
        callerPhone: req.body._metadata?.callerPhone,
        customerId: req.body._metadata?.customerId,
      };

      // Map request body to CallbackToolRequest
      const request = {
        phoneNumber: req.body.phone_number || context.callerPhone,
        scheduledAt: req.body.scheduled_time,
        reason: req.body.reason || 'follow_up',
        reasonDetails: {
          amount: req.body.amount,
          invoiceId: req.body.invoice_id,
        },
        customerName: req.body.customer_name,
        agentContext: req.body.notes,
      };

      const result = await handleScheduleCallbackTool(
        request,
        context,
        services.callbackScheduler
      );

      res.json(result);
    } catch (error: any) {
      console.error('[Database Connector] Callback tool error:', error);
      res.status(500).json({
        success: false,
        message: 'حدث خطأ في جدولة المكالمة',
      });
    }
  });

  // ============================================================
  // Tool Configuration Routes (for agent setup)
  // ============================================================

  router.get('/tools/config/:agentId', authenticateToken, async (req: any, res: Response) => {
    try {
      const domain = process.env.APP_DOMAIN || 'http://localhost:3000';
      const secret = process.env.DB_CONNECTOR_WEBHOOK_SECRET || EncryptionService.generateSecret(16);
      const agentId = req.params.agentId;

      const tools = [
        getQueryDatabaseTool(domain, secret, agentId),
        getScheduleCallbackTool(domain, secret, agentId),
      ];

      res.json({ tools });
    } catch (error: any) {
      res.status(500).json({ error: error.message });
    }
  });

  // Register routes
  app.use('/api/plugins/database-connector', router);

  console.log('[Database Connector] Routes registered at /api/plugins/database-connector');
}

/**
 * Cleanup on shutdown
 */
export async function shutdownDatabaseConnector(): Promise<void> {
  if (pluginServices) {
    pluginServices.callbackScheduler.stop();
    await ConnectionManagerService.closeAllConnections();
    pluginServices = null;
    console.log('[Database Connector] Plugin shutdown complete');
  }
}

// Export services for external use
export { ConnectionManagerService, CallbackSchedulerService, DocumentGeneratorService, EncryptionService };
